import { useErrorStore } from "../../stores/errorStore";
import { getRouter } from "../../router/routerInstance";
import { ERROR_NAVIGATION_MAP } from "../constants/errorConstants";
import { logger } from "../../infrastructure/services/LoggerService";
import { getPostHogService } from "../../infrastructure/services/postHogInstance";
/**
 * Global error handler utility providing consistent error handling across the application
 */
export class ErrorHandler {
    static getErrorStore() {
        return useErrorStore();
    }
    /**
     * Send error to PostHog for error tracking
     */
    static captureExceptionInPostHog(error, context) {
        const postHog = getPostHogService();
        if (postHog?.isInitialized()) {
            const properties = context ? { context } : undefined;
            postHog.captureException(error, properties);
        }
    }
    /**
     * Handle and log a general error
     * @param error - The error to handle
     * @param context - Optional context for better error tracking
     * @returns The formatted error state
     */
    static handle(error, context) {
        if (context) {
            logger.debug(`Error in ${context}:`, error);
        }
        this.captureExceptionInPostHog(error, context);
        return this.getErrorStore().error(error);
    }
    /**
     * Handle API errors with smart navigation logic
     * @param error - The API error to handle
     * @param context - Context information about where the error occurred
     * @param forceNavigation - Override automatic navigation decision
     * @returns The formatted error state
     */
    static handleApiError(error, context, forceNavigation) {
        const contextMessage = context ? ` in ${context}` : "";
        logger.debug(`API Error${contextMessage}:`, error);
        const errorState = this.getErrorStore().error(error);
        // Determine if we should navigate to error page
        const shouldNavigate = this.shouldNavigateToErrorPage(errorState, forceNavigation);
        if (shouldNavigate) {
            this.navigateToErrorPage();
        }
        return errorState;
    }
    /**
     * Handle Adyen payment errors with simplified user-friendly messages
     * @param error - The Adyen error to handle
     * @param context - Context information about where the error occurred
     * @param forceNavigation - Override automatic navigation decision
     * @returns The formatted error state
     */
    static handleAdyenError(error, context, forceNavigation) {
        const contextMessage = context ? ` in ${context}` : "";
        logger.debug(`Adyen Error${contextMessage}:`, error);
        this.captureExceptionInPostHog(error, context ? `Adyen Error in ${context}` : "Adyen Error");
        // Create simplified error with original error preserved
        const err = error;
        const simplifiedError = {
            message: "Payment processing stopped. Adyen component initialization failed. Please check your payment link and try again",
            errorType: "PAYMENT_ERROR",
            originalError: err?.message || String(error) || "Unknown error",
        };
        logger.debug("Simplified Adyen error:", simplifiedError);
        const errorState = this.getErrorStore().error(simplifiedError);
        // Determine if we should navigate to error page
        const shouldNavigate = this.shouldNavigateToErrorPage(errorState, forceNavigation);
        if (shouldNavigate) {
            this.navigateToErrorPage();
        }
        return errorState;
    }
    /**
     * Determine if error should trigger navigation to error page
     * @param errorState - The formatted error state
     * @param forceNavigation - Override automatic decision
     * @returns Boolean indicating if navigation should occur
     */
    static shouldNavigateToErrorPage(errorState, forceNavigation) {
        // If force navigation is explicitly set, use that
        if (typeof forceNavigation === "boolean") {
            return forceNavigation;
        }
        // Use error type mapping for automatic decision
        if (errorState?.type && ERROR_NAVIGATION_MAP[errorState.type] !== undefined) {
            return ERROR_NAVIGATION_MAP[errorState.type];
        }
        // Default to navigation for unknown errors
        return true;
    }
    /**
     * Navigate to error page with proper setup
     */
    static navigateToErrorPage() {
        // Set flag to prevent automatic redirects from error page
        sessionStorage.setItem("stay-on-error-page", "true");
        // Detect if we're in the payment app context and route to the appropriate error page
        const isPaymentContext = window.location.pathname.startsWith("/payment/");
        const errorPath = isPaymentContext ? "/payment/error" : "/error";
        getRouter()
            .push(errorPath)
            .catch((err) => {
            logger.debug("Failed to navigate to error page:", err);
        });
    }
    /**
     * Handle payment refused errors with specific UI and navigation
     * @param cancelUrl - URL to redirect when user cancels (goes back to merchant site)
     * @param retryUrl - URL to redirect when user wants to retry payment
     * @returns The formatted error state
     */
    static handlePaymentRefused(cancelUrl, retryUrl) {
        logger.debug("Payment refused - navigating to error page with retry and cancel options");
        const errorState = this.getErrorStore().setPaymentRefusedError(cancelUrl, retryUrl);
        // Navigate to error page
        this.navigateToErrorPage();
        return errorState;
    }
    /**
     * Handle payment canceled errors with specific UI and navigation
     * @param cancelUrl - URL to redirect to merchant site
     * @returns The formatted error state
     */
    static handlePaymentCanceled(cancelUrl) {
        logger.debug("Payment canceled - navigating to error page");
        const errorState = this.getErrorStore().setPaymentCanceledError(cancelUrl);
        // Navigate to error page
        this.navigateToErrorPage();
        return errorState;
    }
    /**
     * Handle network errors specifically
     * @param error - The network error to handle
     * @param context - Optional context for better tracking
     * @returns The formatted error state
     */
    static handleNetworkError(error, context) {
        logger.debug(`Network Error${context ? ` in ${context}` : ""}:`, error);
        this.captureExceptionInPostHog(error, context ? `Network Error in ${context}` : "Network Error");
        return this.getErrorStore().error(error);
    }
    /**
     * Handle authentication errors with automatic navigation
     * @param error - The authentication error
     * @param context - Optional context information
     * @returns The formatted error state
     */
    static handleAuthError(error, context) {
        logger.debug(`Authentication Error${context ? ` in ${context}` : ""}:`, error);
        this.captureExceptionInPostHog(error, context ? `Auth Error in ${context}` : "Auth Error");
        const errorState = this.getErrorStore().error(error);
        // Authentication errors should always navigate to error page
        this.navigateToErrorPage();
        return errorState;
    }
    /**
     * Handle validation errors (typically for inline display)
     * @param error - The validation error
     * @param context - Optional context information
     * @returns The formatted error state
     */
    static handleValidationError(error, context) {
        logger.debug(`Validation Error${context ? ` in ${context}` : ""}:`, error);
        this.captureExceptionInPostHog(error, context ? `Validation Error in ${context}` : "Validation Error");
        // Validation errors typically don't navigate to error page
        return this.getErrorStore().error(error);
    }
    /**
     * Clear current error state
     */
    static clearCurrent() {
        this.getErrorStore().clearCurrentError();
    }
    /**
     * Clear all error history
     */
    static clearHistory() {
        this.getErrorStore().clearErrorHistory();
    }
    /**
     * Check if there's currently an active error
     */
    static hasError() {
        return this.getErrorStore().hasError;
    }
}
/**
 * Composable for error handling in Vue components
 * Provides convenient access to error handling methods and state
 */
export function useErrorHandler() {
    const errorStore = useErrorStore();
    const handleError = (error, context) => {
        return ErrorHandler.handle(error, context);
    };
    const handleApiError = (error, context, forceNavigation) => {
        return ErrorHandler.handleApiError(error, context, forceNavigation);
    };
    const handleAdyenError = (error, context, forceNavigation) => {
        return ErrorHandler.handleAdyenError(error, context, forceNavigation);
    };
    const handlePaymentRefused = (cancelUrl, retryUrl) => {
        return ErrorHandler.handlePaymentRefused(cancelUrl, retryUrl);
    };
    const handlePaymentCanceled = (cancelUrl) => {
        return ErrorHandler.handlePaymentCanceled(cancelUrl);
    };
    const handleNetworkError = (error, context) => {
        return ErrorHandler.handleNetworkError(error, context);
    };
    const handleAuthError = (error, context) => {
        return ErrorHandler.handleAuthError(error, context);
    };
    const handleValidationError = (error, context) => {
        return ErrorHandler.handleValidationError(error, context);
    };
    return {
        // Error state (reactive)
        currentError: errorStore.currentError,
        errorHistory: errorStore.errorHistory,
        hasError: errorStore.hasError,
        latestError: errorStore.latestError,
        // Error handling methods
        handleError,
        handleApiError,
        handleAdyenError,
        handlePaymentRefused,
        handlePaymentCanceled,
        handleNetworkError,
        handleAuthError,
        handleValidationError,
        // Utility methods
        clearError: ErrorHandler.clearCurrent,
        clearHistory: ErrorHandler.clearHistory,
        checkHasError: ErrorHandler.hasError,
    };
}
// Export error constants for easy access
export { ERROR_CONTEXTS, ERROR_NAVIGATION_MAP, ERROR_MESSAGE_KEYS, } from "../constants/errorConstants";
